import { BrainActionSummary } from "../clientBrainActions/BrainActionSummary";
import { DataQuerySummary } from "../workflowData/DataQuerySummary";
import { StatisticsSummary } from "../workflowData/StatisticsSummary";
import { useMemo, useRef, useState } from "react";
import type { SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import { NoteWorkflowsCanvas } from "../../components/NoteWorkflows/NoteWorkflowsCanvas";
import type { WorkflowNodeSelection } from "../../components/NoteWorkflows/noteWorkflowsTypes";
import { Modal } from "../../components/ui";
import { BUILDER_TRIGGER_NODE_ID, builderWorkflowDocument, getSelectedBuilderBox } from "./builderWorkflowDocument";
import type { BuilderWorkflow, EditWorkflowNodeRequest } from "./models";
import { WorkflowNodeEditor } from "./WorkflowNodeEditor";
import { ProjectSourceBadges } from "./ProjectSourceBadges";
import styles from "./WorkflowBuilder.module.css";

type Props = {
    workflow: BuilderWorkflow;
    draftId: string;
    variant?: "compact" | "detail";
    editor?: {
        revision: number;
        disabled: boolean;
        saving: boolean;
        onSave: (request: EditWorkflowNodeRequest) => Promise<void>;
    };
};

export function WorkflowBuilderCanvas({ workflow, draftId, variant = "compact", editor }: Props) {
    const { t } = useTranslation();
    const [selected, setSelected] = useState<WorkflowNodeSelection | null>(null);
    const canvas = useRef<HTMLDivElement>(null);
    const selectedElement = useRef<HTMLElement | null>(null);
    const document = useMemo(() => builderWorkflowDocument(workflow, draftId), [workflow, draftId]);
    const box = getSelectedBuilderBox(workflow, draftId, selected);
    const triggerSelected = selected?.workflowId === draftId && selected.nodeKind === "trigger" && selected.nodeId === BUILDER_TRIGGER_NODE_ID;
    const selectedNode = triggerSelected ? { title: workflow.title, description: workflow.description, boxId: null }
        : box ? { title: box.title, description: box.instructions, boxId: box.id } : null;
    const validSelection = selectedNode ? selected : null;
    const boxMetadata = box && <>
        <p className={styles.muted}>{t(box.output_type === "select" ? "workflowBuilder.selectOutput" : "workflowBuilder.textOutput")}</p>
        <ProjectSourceBadges selectors={box.context_selectors} />
        <StatisticsSummary query={box.analytics} />
        <DataQuerySummary query={workflow.data_query} timezone={workflow.timezone} />
        <BrainActionSummary action={box.client_brain_action} />
        {box.output_type === "select" && <ul className={styles.nodeOptions}>{box.options.map(option => <li key={option.value}>{option.label}</li>)}</ul>}
    </>;
    // Refit when the chain changes, while retaining the viewport for text edits.
    const topology = JSON.stringify(workflow.boxes.map(item => item.id));
    const rememberNode = (event: SyntheticEvent<HTMLDivElement>) => {
        const node = event.target instanceof Element ? event.target.closest<HTMLElement>(".react-flow__node") : null;
        if (node && event.currentTarget.contains(node)) selectedElement.current = node;
    };

    return <>
        <div ref={canvas} className={variant === "detail" ? styles.detailsCanvas : styles.canvas} aria-label={t("workflowBuilder.boardTitle")} tabIndex={-1} onClickCapture={rememberNode} onFocusCapture={rememberNode}>
            <NoteWorkflowsCanvas key={`${draftId}:${topology}`} document={document} selected={validSelection}
                onNodeSelect={selection => { if (!editor?.disabled) setSelected(selection); }} variant="fullscreen" activateOnKeyboard />
        </div>
        <Modal open={Boolean(validSelection)} onOpenChange={open => { if (!open && !editor?.saving) setSelected(null); }} size="md"
            className={editor ? styles.nodeEditorModal : undefined}
            onCloseAutoFocus={event => {
                event.preventDefault();
                (selectedElement.current?.isConnected ? selectedElement.current : canvas.current)?.focus();
            }}>
            <Modal.Header
                title={triggerSelected ? workflow.title : box?.title}
                description={t(triggerSelected ? "workflowBuilder.triggerNode" : "workflowBuilder.stepNode")}
                onClose={!editor?.saving}
            />
            {editor && validSelection && selectedNode ? <WorkflowNodeEditor
                key={`${draftId}:${validSelection.nodeKind}:${validSelection.nodeId}`}
                title={selectedNode.title}
                description={selectedNode.description}
                boxId={selectedNode.boxId}
                revision={editor.revision} disabled={editor.disabled}
                onCancel={() => setSelected(null)}
                onSave={async request => { await editor.onSave(request); setSelected(null); }}
            >{boxMetadata}</WorkflowNodeEditor> : <Modal.Body>
                <p className={styles.nodeInstructions}>{triggerSelected ? workflow.description : box?.instructions}</p>
                {boxMetadata}
            </Modal.Body>}
        </Modal>
    </>;
}
